import React, { useState, useEffect, useCallback } from 'react';
import {
  Button,
  DatePicker,
  PdfUpload,
  SearchableDropdown,
  SideBarModal,
} from '@/components';
import { ConfirmDialog } from '@components/ConfirmDialog';
import { getBusinessUnitLabel, Handover } from '@/types/common';
import {
  ReturnHandoverRequest,
  HandoverChecklistItemInput,
  RentalTerminationImpact,
  getReturnImpact,
  returnHandover,
} from '@/services/api/handovers';
import { formatRupiah } from '@/utils/format';
import HandoverPartChecklist from './AddHandoverModal/HandoverPartChecklist';
import {
  showSuccessToast,
  showErrorToast,
} from '@/components/toast/globalToast';
import {
  extractApiErrorMessage,
  openImageFileDialog,
  getBucketUrlImageV2,
  uploadFileImage,
  getUserEmail,
} from '@/utils';
import { v4 as uuidv4 } from 'uuid';

interface ReturnHandoverModalProps {
  isOpen: boolean;
  handover: Handover | null;
  successMessage?: string;
  errorMessage?: string;
  onClose: () => void;
  onSuccess: () => void;
  onSubmitOverride?: (data: ReturnHandoverRequest) => Promise<void>;
  allowedReturnReasons?: ReturnReason[];
}

type ReturnReason =
  | 'ACCIDENT'
  | 'CONTRACT_END'
  | 'MITRA_RESIGN'
  | 'CLIENT_REQUEST'
  | 'DUTY_COMPLETED'
  | 'REPOSSESSION'
  | 'OTHER';

interface FormData {
  returnDate: string;
  returnOdometer: string;
  returnCondition: 'GOOD' | 'MINOR_DAMAGE' | 'MAJOR_DAMAGE';
  returnReason: ReturnReason;
  returnReasonOther?: string;
  returnBastLink: string;
  returnNotes: string;
  vehicleReturnImageURL: string;
  vehicleAndMitraReturnImageURL: string;
  unitEquipment: string[];
  returnUnitEquipment: string[];
}

const returnReasons = [
  { value: 'ACCIDENT', label: 'Accident' },
  { value: 'CONTRACT_END', label: 'Contract End' },
  { value: 'MITRA_RESIGN', label: 'Mitra Resign' },
  { value: 'CLIENT_REQUEST', label: 'Client Request' },
  { value: 'DUTY_COMPLETED', label: 'Selesai Bertugas' },
  { value: 'REPOSSESSION', label: 'Penarikan' },
  { value: 'OTHER', label: 'Other (please specify)' },
] as const satisfies ReadonlyArray<{ value: ReturnReason; label: string }>;

const defaultAllowedReturnReasons: ReturnReason[] = ['ACCIDENT', 'OTHER'];

const vehicleConditions = [
  { value: 'GOOD', label: 'Good' },
  { value: 'MINOR_DAMAGE', label: 'Minor Damage' },
  { value: 'MAJOR_DAMAGE', label: 'Major Damage' },
];

const defaultEquipmentOptions = [
  'Kaca Spion Kiri',
  'Kaca Spion Kanan',
  'Kunci Utama',
  'Kunci Cadangan',
  'Remote',
  'STNK',
  'Plat Nomor',
  'Battery',
  'Charger',
  'Unit',
  'Other',
];

const TrashIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    className="text-gray-500 hover:text-red-500"
  >
    <polyline points="3 6 5 6 21 6" />
    <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
  </svg>
);

const ReturnHandoverModal: React.FC<ReturnHandoverModalProps> = ({
  isOpen,
  handover,
  successMessage = 'Handover returned successfully',
  errorMessage = 'Failed to return handover',
  onClose,
  onSuccess,
  onSubmitOverride,
  allowedReturnReasons = defaultAllowedReturnReasons,
}) => {
  const defaultReturnReason = allowedReturnReasons[0] || 'ACCIDENT';
  const availableReturnReasons = returnReasons.filter((reason) =>
    allowedReturnReasons.includes(reason.value)
  );
  const resolveReturnReason = (value?: string): ReturnReason =>
    availableReturnReasons.some((reason) => reason.value === value)
      ? (value as ReturnReason)
      : defaultReturnReason;

  const [formData, setFormData] = useState<FormData>({
    returnDate: new Date().toISOString().split('T')[0],
    returnOdometer: '',
    returnCondition: 'GOOD',
    returnReason: defaultReturnReason,
    returnReasonOther: '',
    returnBastLink: '',
    returnNotes: '',
    vehicleReturnImageURL: '',
    vehicleAndMitraReturnImageURL: '',
    unitEquipment: [],
    returnUnitEquipment: [],
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isImageUploading, setIsImageUploading] = useState(false);
  const [thumbnail, setThumbnail] = useState<string>('');
  const [isDocImageUploading, setIsDocImageUploading] = useState(false);
  const [docThumbnail, setDocThumbnail] = useState<string>('');
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  // Diambil saat ops menekan Confirm Return, bukan saat modal dibuka:
  // angkanya bergantung pada alasan dan tanggal return, yang keduanya masih
  // bisa diganti sampai detik terakhir.
  const [rentalImpact, setRentalImpact] =
    useState<RentalTerminationImpact | null>(null);
  const [isLoadingImpact, setIsLoadingImpact] = useState(false);
  const [showErrors, setShowErrors] = useState(false);

  // Structured per-part return checklist emitted by HandoverPartChecklist.
  const [checklistItems, setChecklistItems] = useState<
    HandoverChecklistItemInput[]
  >([]);
  const [checklistComplete, setChecklistComplete] = useState(false);

  const handleChecklistChange = useCallback(
    (items: HandoverChecklistItemInput[], complete: boolean) => {
      setChecklistItems(items);
      setChecklistComplete(complete);
    },
    []
  );

  useEffect(() => {
    if (handover && isOpen) {
      console.log('Handover data:', handover); // Debug log

      // Get equipment from handover data
      const handoverEquipment = handover?.unitEquipment;

      console.log('Handover equipment found:', handoverEquipment); // Debug log

      // Filter to include items that exist in defaultEquipmentOptions OR are custom "Other -" items
      const validEquipment = handoverEquipment.filter(
        (equipment: string) =>
          defaultEquipmentOptions.includes(equipment) ||
          equipment.startsWith('Other - ')
      );

      console.log('Valid equipment after filtering:', validEquipment); // Debug log

      setFormData((prev) => ({
        ...prev,
        unitEquipment: validEquipment,
        returnUnitEquipment: validEquipment,
        returnReason: resolveReturnReason((handover as any)?.returnReason),
        returnReasonOther: (handover as any)?.returnReasonOther || '',
      }));
    }
    // resolveReturnReason is intentionally omitted: it derives from the
    // `allowedReturnReasons` prop, which every caller passes as an inline
    // array literal, so it is a new identity on each parent render. Adding it
    // would re-run this effect every render and the setFormData above would
    // loop. This should only re-sync when the modal opens or the handover
    // changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [handover, isOpen, defaultReturnReason]);

  const handleInputChange = (
    field: keyof FormData,
    value: string | string[]
  ) => {
    // Validate return odometer to prevent negative values
    if (field === 'returnOdometer' && typeof value === 'string') {
      const numericValue = parseFloat(value);
      if (numericValue < 0) {
        return; // Don't update if the value is negative
      }
    }

    setFormData((prev) => ({
      ...prev,
      [field]: value,
      ...(field === 'returnReason' && value !== 'OTHER'
        ? { returnReasonOther: '' }
        : {}),
    }));
  };

  const handleImageUpload = async () => {
    openImageFileDialog(
      async (file: { name: string; base64: string; size: number } | null) => {
        try {
          setIsImageUploading(true);

          if (!file) {
            setIsImageUploading(false);
            return;
          }

          const uuid = uuidv4();

          const response = await getBucketUrlImageV2({
            type: 'vehicleHandover',
            uuid: uuid,
          });

          if (!response?.data) {
            throw new Error('Failed to get upload URL');
          }

          const url = response.data;
          const base64Data = file?.base64?.split(',')[1];

          if (!base64Data) {
            throw new Error('Invalid file data');
          }

          const upload = await uploadFileImage(url, base64Data, file?.name);

          if (upload.status === 200) {
            setThumbnail(url);
            handleInputChange('vehicleReturnImageURL', url);
            showSuccessToast('Success', 'Upload image success');
          } else {
            throw new Error('Upload failed');
          }
        } catch (error: any) {
          const errorMessage = extractApiErrorMessage(
            error,
            'Failed to upload image'
          );
          showErrorToast('Error', errorMessage);
        } finally {
          setIsImageUploading(false);
        }
      }
    );
  };

  const handleSubmit = () => {
    // Every checklist item must be resolved (Ada / Tidak ada, plus photos when
    // web_fms_handover_checklist_require_photo is on).
    if (!checklistComplete) {
      showErrorToast(
        'Error',
        'Lengkapi checklist: tandai tiap kelengkapan dan foto yang diperlukan.'
      );
      return;
    }

    const hasBastLink = Boolean(formData.returnBastLink);
    const hasReturnImage = Boolean(formData.vehicleReturnImageURL);
    const hasDocumentationImage = Boolean(
      formData.vehicleAndMitraReturnImageURL
    );
    if (!hasBastLink || !hasReturnImage || !hasDocumentationImage) {
      setShowErrors(true);
      return;
    }

    void loadImpactThenConfirm();
  };

  // Gagal memuat preview tetap membuka dialog tanpa blok kontrak: yang
  // menahan penutupan kontrak tanpa persetujuan adalah backend, bukan ini.
  const loadImpactThenConfirm = async () => {
    if (!handover?.ID) return;
    setIsLoadingImpact(true);
    try {
      setRentalImpact(
        await getReturnImpact(
          handover.ID,
          formData.returnReason,
          new Date(formData.returnDate).toISOString()
        )
      );
    } catch {
      setRentalImpact(null);
    } finally {
      setIsLoadingImpact(false);
      setShowConfirmModal(true);
    }
  };

  const handleConfirmSubmit = async () => {
    try {
      setIsSubmitting(true);
      setShowConfirmModal(false);

      if (!handover?.ID) {
        throw new Error('Handover ID is required');
      }

      const userEmail = getUserEmail();

      const submitData: ReturnHandoverRequest = {
        returnDate: new Date(formData.returnDate).toISOString(),
        returnOdometer: formData.returnOdometer
          ? parseInt(formData.returnOdometer)
          : undefined,
        checklistItems,
        returnCondition: formData.returnCondition,
        returnReason: formData.returnReason,
        returnReasonOther:
          formData.returnReason === 'OTHER'
            ? formData.returnReasonOther || undefined
            : undefined,
        updatedBy: userEmail,
        vehicleReturnImageURL: formData.vehicleReturnImageURL || undefined,
        vehicleAndMitraReturnImageURL:
          formData.vehicleAndMitraReturnImageURL || undefined,
        returnBastLink: formData.returnBastLink,
        returnNotes: formData.returnNotes || undefined,
        // Hanya dikirim kalau memang ada kontrak yang akan berakhir, dan
        // hanya sesudah dialog di bawah memperlihatkan akibatnya.
        confirmRentalTermination: rentalImpact ? true : undefined,
      };

      if (onSubmitOverride) {
        await onSubmitOverride(submitData);
      } else {
        await returnHandover(handover.ID, submitData);
      }

      showSuccessToast('Success', successMessage);
      onSuccess();
      handleClose();
    } catch (error) {
      const apiErrorMessage = extractApiErrorMessage(error, errorMessage);
      showErrorToast('Error', apiErrorMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      returnDate: new Date().toISOString().split('T')[0],
      returnOdometer: '',
      returnCondition: 'GOOD',
      returnReason: defaultReturnReason,
      returnReasonOther: '',
      returnBastLink: '',
      returnNotes: '',
      vehicleReturnImageURL: '',
      vehicleAndMitraReturnImageURL: '',
      unitEquipment: [],
      returnUnitEquipment: [],
    });
    setThumbnail('');
    setDocThumbnail('');
    setShowErrors(false);
    setShowConfirmModal(false);
    setRentalImpact(null);
    onClose();
  };

  if (!isOpen || !handover) return null;

  return (
    <>
      <SideBarModal
        isOpen={isOpen}
        onClose={handleClose}
        position="center"
        width="full"
        header={
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Return Handover
            </h2>
            <p className="text-sm font-normal text-gray-600">
              Review and confirm units to be returned.
            </p>
          </div>
        }
        footer={
          <div className="flex gap-3">
            <Button
              variant="neutral"
              onClick={handleClose}
              className="flex-1 py-3"
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSubmit}
              className="flex-1 py-3"
              disabled={
                isSubmitting ||
                isLoadingImpact ||
                isImageUploading ||
                !checklistComplete
              }
            >
              {isSubmitting
                ? 'Processing...'
                : isLoadingImpact
                  ? 'Memeriksa kontrak...'
                  : 'Confirm Return'}
            </Button>
          </div>
        }
      >
        <div className="flex-1 overflow-hidden flex">
          {/* Left Side - Unit Lists */}
          <div className="w-64 border-r border-gray-200 flex flex-col">
            <div className="p-6 border-b border-gray-200">
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  Unit Lists
                </h3>
                <p className="text-xs text-gray-500 mb-4">
                  Select a unit to review its details.
                </p>
              </div>

              {/* Vehicle Info Card */}
              <div className="border-2 border-[#5e2aac] rounded-lg p-3">
                <div className="flex flex-col w-full">
                  <div className="text-sm font-semibold text-gray-900 pb-2 border-b border-gray-200 mb-3">
                    {handover.platNomor || 'Vehicle'}
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div className="flex flex-col">
                      <div className="text-xs text-gray-500 mb-1">OEM Name</div>
                      <div className="text-xs font-semibold text-gray-900 break-words">
                        {handover.oemName || '-'}
                      </div>
                    </div>
                    <div className="flex flex-col">
                      <div className="text-xs text-gray-500 mb-1">
                        Model Name
                      </div>
                      <div className="text-xs font-semibold text-gray-900 break-words">
                        {handover.modelName || '-'}
                      </div>
                    </div>
                    <div className="flex flex-col">
                      <div className="text-xs text-gray-500 mb-1">
                        Hub Location
                      </div>
                      <div className="text-xs font-semibold text-gray-900 break-words">
                        {handover.vehicleHubName || '-'}
                      </div>
                    </div>
                    <div className="flex flex-col">
                      <div className="text-xs text-gray-500 mb-1">
                        Business Unit
                      </div>
                      <div className="text-xs font-semibold text-gray-900 break-words">
                        {getBusinessUnitLabel(handover.vehicleBusinessUnit)}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side - Unit Details */}
          <div className="flex-1 overflow-y-auto">
            <div className="py-6">
              <div className="mb-6 px-6">
                <h3 className="text-sm font-medium text-gray-900 mb-2">
                  Unit Details
                </h3>
                <p className="text-xs text-gray-500 mb-4">
                  Review the equipment return date.
                </p>
              </div>

              {/* Return Info */}
              <div className="space-y-6 px-6">
                {/* Return Date */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Return Date & Time <span className="text-red-500">*</span>
                  </label>
                  <DatePicker
                    value={formData.returnDate}
                    onChange={(value: string) =>
                      handleInputChange('returnDate', value)
                    }
                    placeholder="Select return date and time"
                    showTimeSelect={true}
                    className="w-full"
                  />
                </div>

                {/* Return Odometer */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Return Odometer (km) <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    value={formData.returnOdometer}
                    onChange={(e) =>
                      handleInputChange('returnOdometer', e.target.value)
                    }
                    placeholder="Enter odometer reading"
                    className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Return Reason */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Return Reason <span className="text-red-500">*</span>
                  </label>
                  <div className="space-y-2">
                    {availableReturnReasons.map((reason) => (
                      <label key={reason.value} className="flex items-center">
                        <input
                          type="radio"
                          name="returnReason"
                          value={reason.value}
                          checked={formData.returnReason === reason.value}
                          onChange={(e) =>
                            handleInputChange('returnReason', e.target.value)
                          }
                          className="h-4 w-4 border-gray-300 focus:ring-2 focus:ring-offset-2"
                          style={{
                            accentColor: '#5e2aac',
                          }}
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          {reason.label}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>

                {/* Custom Reason Text */}
                {formData.returnReason === 'OTHER' && (
                  <div>
                    <textarea
                      value={formData.returnReasonOther}
                      onChange={(e) =>
                        handleInputChange('returnReasonOther', e.target.value)
                      }
                      placeholder="Please specify the reason..."
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                  </div>
                )}
              </div>

              {/* Inspection & Checklist */}
              <div className="mt-8">
                <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 rounded-md mb-4">
                  Inspection & Checklist
                </div>

                {/* Vehicle Return Image */}
                <div className="mb-6 px-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Foto Kendaraan <span className="text-red-500">*</span>
                  </label>
                  <div>
                    {isImageUploading ? (
                      <div className="flex flex-col items-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                        <p className="mt-2 text-xs text-gray-600">
                          Uploading...
                        </p>
                      </div>
                    ) : thumbnail ? (
                      <div
                        className={`flex items-center space-x-3 p-2 border-2 border-dashed rounded-lg ${
                          showErrors && !formData.vehicleReturnImageURL
                            ? 'border-red-500'
                            : 'border-gray-300'
                        }`}
                      >
                        <div className="text-gray-600">
                          <svg
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                            className="w-8 h-8"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                        </div>
                        <div className="flex-1">
                          <p className="text-xs font-medium text-gray-800">
                            Vehicle Return Image
                          </p>
                          <p className="text-xs text-green-600">Completed</p>
                        </div>
                        <button
                          onClick={() => {
                            setThumbnail('');
                            handleInputChange('vehicleReturnImageURL', '');
                          }}
                          className="text-gray-400 hover:text-red-500"
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    ) : (
                      <div
                        className={`text-center border-2 border-dashed rounded-lg w-full h-32 flex flex-col items-center justify-center bg-gray-50 ${
                          showErrors && !formData.vehicleReturnImageURL
                            ? 'border-red-500'
                            : 'border-gray-300'
                        }`}
                      >
                        <svg
                          className="w-8 h-8 text-gray-400 mx-auto mb-2"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        <p className="text-xs text-gray-600 mb-2">
                          Choose a file or drag & drop it here.
                        </p>
                        <button
                          onClick={handleImageUpload}
                          className="text-xs bg-white border border-gray-300 text-gray-700 px-3 py-1 rounded hover:bg-gray-50"
                        >
                          Browse File
                        </button>
                      </div>
                    )}
                  </div>
                  {showErrors && !formData.vehicleReturnImageURL && (
                    <p className="mt-1 text-xs text-red-500">
                      Photo is required.
                    </p>
                  )}
                </div>

                {/* Vehicle Condition */}
                <div className="mb-6 px-6">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Vehicle Condition <span className="text-red-500">*</span>
                  </label>
                  <SearchableDropdown
                    options={vehicleConditions}
                    value={formData.returnCondition}
                    onChange={(value) =>
                      handleInputChange('returnCondition', value)
                    }
                    placeholder="Search conditions or select..."
                    emptyMessage="No conditions found"
                    className="w-full"
                  />
                </div>

                {/* Per-part return checklist */}
                <HandoverPartChecklist onChange={handleChecklistChange} />
              </div>

              {/* Return Documentation */}
              <div className="mt-8">
                <div className="bg-gray-50 px-4 py-2 text-sm font-medium text-gray-700 rounded-md mb-4">
                  Return Documentation
                </div>

                <div className="space-y-6 px-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Document BAST <span className="text-red-500">*</span>
                    </label>
                    <PdfUpload
                      value={formData.returnBastLink}
                      fileType="vehicleReturn"
                      onUploaded={(url) =>
                        handleInputChange('returnBastLink', url)
                      }
                      onRemove={() => handleInputChange('returnBastLink', '')}
                      error={
                        showErrors && !formData.returnBastLink
                          ? 'BAST document is required'
                          : undefined
                      }
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Foto Mitra & Kendaraan{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    <div>
                      {isDocImageUploading ? (
                        <div className="flex flex-col items-center">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                          <p className="mt-2 text-xs text-gray-600">
                            Uploading...
                          </p>
                        </div>
                      ) : docThumbnail ? (
                        <div
                          className={`flex items-center space-x-3 p-2 border-2 border-dashed rounded-lg ${
                            showErrors &&
                            !formData.vehicleAndMitraReturnImageURL
                              ? 'border-red-500'
                              : 'border-gray-300'
                          }`}
                        >
                          <div className="text-gray-600">
                            <svg
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                              className="w-8 h-8"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                              />
                            </svg>
                          </div>
                          <div className="flex-1">
                            <p className="text-xs font-medium text-gray-800">
                              Return Documentation Image
                            </p>
                            <p className="text-xs text-green-600">Completed</p>
                          </div>
                          <button
                            onClick={() => {
                              setDocThumbnail('');
                              handleInputChange(
                                'vehicleAndMitraReturnImageURL',
                                ''
                              );
                            }}
                            className="text-gray-400 hover:text-red-500"
                          >
                            <TrashIcon />
                          </button>
                        </div>
                      ) : (
                        <div
                          className={`text-center border-2 border-dashed rounded-lg w-full h-32 flex flex-col items-center justify-center bg-gray-50 ${
                            showErrors &&
                            !formData.vehicleAndMitraReturnImageURL
                              ? 'border-red-500'
                              : 'border-gray-300'
                          }`}
                        >
                          <svg
                            className="w-8 h-8 text-gray-400 mx-auto mb-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                          </svg>
                          <p className="text-xs text-gray-600 mb-2">
                            Choose a file or drag & drop it here.
                          </p>
                          <button
                            onClick={() => {
                              openImageFileDialog(async (file) => {
                                try {
                                  setIsDocImageUploading(true);

                                  if (!file) {
                                    setIsDocImageUploading(false);
                                    return;
                                  }

                                  const uuid = uuidv4();

                                  const response = await getBucketUrlImageV2({
                                    type: 'vehicleHandover',
                                    uuid: uuid,
                                  });

                                  if (!response?.data) {
                                    throw new Error('Failed to get upload URL');
                                  }

                                  const url = response.data;
                                  const base64Data =
                                    file?.base64?.split(',')[1];

                                  if (!base64Data) {
                                    throw new Error('Invalid file data');
                                  }

                                  const upload = await uploadFileImage(
                                    url,
                                    base64Data,
                                    file?.name
                                  );

                                  if (upload.status === 200) {
                                    setDocThumbnail(url);
                                    handleInputChange(
                                      'vehicleAndMitraReturnImageURL',
                                      url
                                    );
                                    showSuccessToast(
                                      'Success',
                                      'Upload image success'
                                    );
                                  } else {
                                    throw new Error('Upload failed');
                                  }
                                } catch (error: any) {
                                  const errorMessage = extractApiErrorMessage(
                                    error,
                                    'Failed to upload image'
                                  );
                                  showErrorToast('Error', errorMessage);
                                } finally {
                                  setIsDocImageUploading(false);
                                }
                              });
                            }}
                            className="text-xs bg-white border border-gray-300 text-gray-700 px-3 py-1 rounded hover:bg-gray-50"
                          >
                            Browse File
                          </button>
                        </div>
                      )}
                    </div>
                    {showErrors && !formData.vehicleAndMitraReturnImageURL && (
                      <p className="mt-1 text-xs text-red-500">
                        Photo is required.
                      </p>
                    )}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Notes
                    </label>
                    <textarea
                      value={formData.returnNotes}
                      onChange={(e) =>
                        handleInputChange('returnNotes', e.target.value)
                      }
                      placeholder="e.g. Motor dijemput oleh tim Electrum"
                      rows={4}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none"
                    />
                    <div className="text-right text-xs text-gray-500 mt-1">
                      {formData.returnNotes.length}/200
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* `ConfirmDialog` is its own <dialog> opened with showModal(), so it
            stacks in the top layer above this sheet — no portal needed. */}
        <ConfirmDialog
          isOpen={showConfirmModal}
          title={
            rentalImpact
              ? 'Kembalikan unit & hentikan kontrak?'
              : 'Return this unit?'
          }
          description="This will mark the selected unit as returned. Make sure all equipment items are verified before proceeding."
          cancelLabel="Cancel"
          confirmLabel={
            isSubmitting
              ? 'Processing...'
              : rentalImpact
                ? 'Kembalikan & Hentikan Kontrak'
                : 'Return Unit'
          }
          isBusy={isSubmitting}
          onCancel={() => setShowConfirmModal(false)}
          onConfirm={handleConfirmSubmit}
        >
          {/* Dampak ke kontrak rent. Hanya muncul kalau unitnya memang masih
              terikat kontrak; unit FMS biasa tidak melihat blok ini sama
              sekali. */}
          {rentalImpact && (
            <div className="mt-4 rounded-lg border border-orange-200 bg-orange-50 p-4">
              <p className="text-sm font-medium text-gray-900">
                Kontrak {rentalImpact.rentalCode}
                {rentalImpact.driverName
                  ? ` — ${rentalImpact.driverName}`
                  : ''}{' '}
                akan diberhentikan
              </p>
              <dl className="mt-3 space-y-1.5 text-sm">
                <div className="flex justify-between gap-4">
                  <dt className="text-gray-600">Status kontrak</dt>
                  <dd className="font-medium text-gray-900">
                    Selesai ({rentalImpact.terminationReason})
                  </dd>
                </div>
                {rentalImpact.arrearsOwed > 0 && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-600">
                      Tunggakan jadi tagihan
                      {rentalImpact.arrearsDays > 0
                        ? ` (${rentalImpact.arrearsDays} hari)`
                        : ''}
                    </dt>
                    <dd className="font-medium text-gray-900">
                      {formatRupiah(rentalImpact.arrearsOwed)}
                    </dd>
                  </div>
                )}
                {rentalImpact.chargesVoided > 0 && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-600">
                      Tagihan belum jatuh tempo, dibatalkan
                    </dt>
                    <dd className="font-medium text-gray-900">
                      {formatRupiah(rentalImpact.chargesVoided)}
                    </dd>
                  </div>
                )}
                {rentalImpact.prepaidToRefund > 0 && (
                  <div className="flex justify-between gap-4">
                    <dt className="text-gray-600">
                      Saldo prepaid dikembalikan
                    </dt>
                    <dd className="font-medium text-gray-900">
                      {formatRupiah(rentalImpact.prepaidToRefund)}
                    </dd>
                  </div>
                )}
              </dl>
              {rentalImpact.arrearsOwed === 0 && (
                <p className="mt-3 text-xs text-gray-600">
                  Tidak ada tunggakan, jadi tidak ada tagihan outstanding yang
                  dibuat.
                </p>
              )}
              {rentalImpact.outstandingAlreadyExists && (
                <p className="mt-3 text-xs text-gray-600">
                  Tagihan outstanding untuk kontrak ini sudah ada, jadi tidak
                  akan dibuat dua kali.
                </p>
              )}
            </div>
          )}
        </ConfirmDialog>
      </SideBarModal>
    </>
  );
};

export default ReturnHandoverModal;
